import { create } from 'zustand';
import keycloak from '../services/authService';
import { User } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
  init: () => Promise<void>;
  login: () => void;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: true,

  init: async () => {
    try {
      const authenticated = await keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        checkLoginIframe: false,
      });

      if (authenticated) {
        const user: User = {
          id: keycloak.tokenParsed?.sub || '',
          username: keycloak.tokenParsed?.preferred_username || '',
          email: keycloak.tokenParsed?.email || '',
          full_name: keycloak.tokenParsed?.name || '',
          role: keycloak.tokenParsed?.realm_access?.roles?.[0] || 'learner',
        };

        set({
          isAuthenticated: true,
          user,
          token: keycloak.token,
          isLoading: false,
        });
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      set({ isLoading: false });
    }
  },

  login: () => {
    keycloak.login({
      redirectUri: window.location.origin,
    });
  },

  logout: () => {
    set({
      isAuthenticated: false,
      user: null,
      token: null,
    });
    keycloak.logout({
      redirectUri: window.location.origin,
    });
  },

  refreshToken: async () => {
    try {
      await keycloak.updateToken(70); // Refresh if token expires in 70 seconds
      const { token } = get();
      if (token !== keycloak.token) {
        set({ token: keycloak.token });
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      get().logout();
    }
  },
}));